import { CardWrapper } from '@systemeio/digital-store-shared';
import { DataFileInterface, FileSourceEnum, FileTypeEnum, FileItem, getFileTypeFromName } from '@systemeio/file-manager';
import { LecturePage } from '@systemeio/light-lecture-viewer';
import { FieldLabel, FormInput, PrimaryButton, toast, CheckBoxSwitcher } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import ItemSteps from 'modules/stores/store/components/item-steps';
import { StoreTextEditor } from 'modules/stores/store/components/store-text-editor';
import { useStorePreviewContext } from 'modules/stores/store/context/store-preview-context';
import { ChangeEventHandler, useEffect, useState } from 'react';
import { FileUploader } from 'shared/components/file-uploader/file-uploader';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { useLightCourseActions } from '../hooks/use-light-course-actions';
import { useLightCourseCurriculum } from '../hooks/use-light-course-curriculum';
import { useLightCourseLecture } from '../hooks/use-light-course-lecture';
import { LightCourseLectureInterface, UpdateErrorsLightCourseLectureInterface } from '../light-course-interface';
import { twJoin } from 'tailwind-merge';
export interface LightCourseLectureEditorProps {
  courseId?: number;
  lectureId?: number;
  hideStepsNumbers?: boolean;
  hideSaveButton?: boolean;
  useSectionHeading?: boolean;
  onStatusChange?: (_: {
    isFetching: boolean;
    tempState?: LightCourseLectureInterface;
  }) => void;
  afterSubmit?: () => void;
}
export const LightCourseLectureEditor = ({
  courseId,
  lectureId,
  hideStepsNumbers,
  hideSaveButton,
  useSectionHeading,
  onStatusChange,
  afterSubmit
}: LightCourseLectureEditorProps) => {
  const {
    t
  } = useLocoTranslation();
  const {
    lecture,
    mutate: mutateLecture
  } = useLightCourseLecture(lectureId);
  const {
    patchLightCourseLecture
  } = useLightCourseActions();
  const {
    mutate: mutateCurriculum
  } = useLightCourseCurriculum(courseId);
  const [isFetching, setIsFetching] = useState(false);
  const [tempState, setTempState] = useState<LightCourseLectureInterface>();
  const [errors, setErrors] = useState<UpdateErrorsLightCourseLectureInterface>();
  useEffect(() => {
    if (!lecture) return;
    setTempState(lecture);
  }, [lecture?.id]);
  useEffect(() => {
    if (onStatusChange) {
      onStatusChange({
        isFetching,
        tempState
      });
    }
  }, [isFetching, tempState]);
  const handleNameChange: ChangeEventHandler<HTMLInputElement> = async event => {
    setTempState(prev => prev && {
      ...prev,
      name: event.target.value
    });
  };
  const handleShowCommentChange = async (showComments: boolean) => {
    setTempState(prev_0 => prev_0 && {
      ...prev_0,
      showComments
    });
  };
  const handleBodyChange = async (body: string) => {
    setTempState(prev_1 => prev_1 && {
      ...prev_1,
      body
    });
  };
  const handleVideoChange = async (file?: DataFileInterface) => {
    setTempState(prev_2 => prev_2 && {
      ...prev_2,
      video: file ?? null
    });
  };
  const handleAttachmentsChange = async (file_0: DataFileInterface | number) => {
    if (typeof file_0 === 'number') {
      setTempState(prev_3 => prev_3 && {
        ...prev_3,
        attachments: prev_3.attachments.filter(a => a.id !== file_0)
      });
      return;
    }
    setTempState(prev_4 => prev_4 && {
      ...prev_4,
      attachments: [...prev_4.attachments, file_0]
    });
  };
  const handleSubmit = async () => {
    if (!tempState || !lectureId) return;
    if (isFetching) return;
    setIsFetching(true);
    setErrors(undefined);
    try {
      const updatedLecture = await patchLightCourseLecture(lectureId, {
        name: tempState.name,
        body: tempState.body,
        video: tempState.video?.id ?? null,
        attachments: tempState.attachments.map(attachment => attachment.id),
        showComments: tempState?.showComments ?? false
      });
      await mutateLecture(updatedLecture, false);
      await mutateCurriculum(data => {
        if (!data) return;
        return {
          ...data,
          modules: data.modules.map(module => {
            return {
              ...module,
              lectures: module.lectures.map(lecture_0 => {
                if (lecture_0.id === lectureId) {
                  return {
                    ...lecture_0,
                    name: updatedLecture.name
                  };
                }
                return lecture_0;
              })
            };
          })
        };
      }, false);
      toast.success(t('dashboard.light_course.lecture_changes_saved'));
      afterSubmit?.();
    } catch (error) {
      if (error instanceof BadRequest) {
        setErrors(error.errors.fields);
        return;
      }
    } finally {
      setIsFetching(false);
    }
  };
  const {
    setSingleItemMode,
    setPreview,
    lightLectureCss
  } = useStorePreviewContext();
  useEffect(() => {
    if (tempState) {
      setSingleItemMode(true);
      setPreview([<CardWrapper key={'preview-lecture'}>
          <div style={{
          width: '100%'
        }}>
            <style>{lightLectureCss}</style>
            <LecturePage name={tempState.name} body={tempState.body} video={tempState.video} attachments={tempState.attachments} showPlaceholderComments={tempState.showComments} />
          </div>
        </CardWrapper>]);
    }
  }, [tempState]);
  const items = [{
    key: 'lecture title',
    title: t('dashboard.light_course.write_description'),
    element: <div className="flex flex-col gap-5">
          <FormInput label={t('dashboard.light_course.lecture_title')} required placeholder={t('dashboard.light_course.lecture_title')}
      // https://app.rollbar.com/a/systemeio/fix/item/dashboard-frontend/12435#detail
      value={tempState?.name ?? ''} onChange={handleNameChange} isPreFetching={!lecture} error={errors?.name} />

          <div className="flex flex-col gap-1">
            <FieldLabel label={t('dashboard.light_course.lecture_body')} required />
            <StoreTextEditor content={tempState?.body ?? ''} onChange={handleBodyChange} isPreFetching={!lecture} className={twJoin(errors?.body !== undefined && 'border-danger')} error={errors?.body} />
          </div>
        </div>
  }, {
    key: 'video',
    title: t('dashboard.light_course.video_upload'),
    element: <FileUploader label={t('dashboard.light_course.drop_video_label')} acceptTypes={[FileTypeEnum.Video]} source={FileSourceEnum.DigitalStore} onSelect={handleVideoChange} isPreFetching={!lecture} error={errors?.video} disabled={isFetching}>
          {tempState?.video && <FileItem file={{
        id: tempState.video.id,
        path: tempState.video.path,
        size: 0,
        type: FileTypeEnum.Video,
        name: tempState.video.name,
        filename: tempState.video.name,
        thumbnail: tempState.video.thumbnail || tempState.video.path
      }} showDeleteButton onDeleteFile={() => handleVideoChange(undefined)} />}
        </FileUploader>
  }, {
    key: 'file',
    title: t('dashboard.light_course.file_upload'),
    element: <FileUploader disabledFileTypeSelect={false} label={t('dashboard.light_course.drop_files_label')} source={FileSourceEnum.DigitalStore} onSelect={handleAttachmentsChange} isPreFetching={!lecture} disabled={isFetching} error={errors?.attachments?.join(', ')}>
          {tempState?.attachments.map(attachment_0 => <FileItem key={attachment_0.id} file={{
        id: attachment_0.id,
        path: attachment_0.path,
        size: 0,
        type: getFileTypeFromName(attachment_0.name),
        name: attachment_0.name,
        filename: attachment_0.name,
        thumbnail: attachment_0.thumbnail || attachment_0.path
      }} showDeleteButton onDeleteFile={() => handleAttachmentsChange(attachment_0.id)} />)}
        </FileUploader>
  }, {
    key: 'comment',
    title: t('dashboard.light_course.comment_section_title'),
    element: <CheckBoxSwitcher label={t('dashboard.light_course.comment_section_lable')} selected={tempState?.showComments || false} onChange={handleShowCommentChange} disabled={isFetching} />
  }];
  return <form id={LightCourseLectureEditor.formId} className="flex flex-col items-end gap-5" onSubmit={e => {
    e.preventDefault();
    handleSubmit();
  }}>
      <ItemSteps steps={items} hideNumbers={hideStepsNumbers} useSectionHeading={useSectionHeading} />

      {!hideSaveButton && <PrimaryButton type="submit" isFetching={isFetching} disabled={!lecture}>
          <span>{t('global.save')}</span>
        </PrimaryButton>}
    </form>;
};
LightCourseLectureEditor.formId = 'light-course-lecture-editor-form';